import { LoadingButton } from '@mui/lab';
import { Box, Card, styled, TextField, Typography } from '@mui/material';

import { Formik, Form } from 'formik';
import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { AlertSnackbarContext } from '../../components/snackbars';
import { AlertMessages, ButtonStrings, Color, ErrorMessageStrings, VerifyOtpStrings } from '../../constants';
import { SignUpToEventDto, useWebSignUpToEvent } from '../../lib/event/hooks/index';
import { useRequestOtp } from '../../lib/user/hooks/useRequestOtp';
import { Event, EventInviteStatus } from '../../types/Event';
import { UserSignUpFormValues } from '../events/EventSignUp';
import { useSignUpFormStore } from '../hooks/persist/useSignUpFormStore';

const validationSchema = Yup.object({
  code: Yup.string().required('Verification code is required').length(4),
});

type VerifyOtpProps = {
  event: Event;
  isSignUp: boolean;
  formValue: UserSignUpFormValues;
  onFinish?: () => void;
};

type VerifyOtpForm = {
  code: string;
};

const RESEND_COOLDOWN_SECONDS = 600; // 10 minutes in seconds

const VerifyOtp = ({ event, isSignUp, formValue, onFinish }: VerifyOtpProps) => {
  const { mutate, isLoading: isSigningUp } = useWebSignUpToEvent();
  const initialValues = useMemo<Partial<VerifyOtpForm>>(() => {
    return {
      code: '',
    };
  }, []);
  const eventKey = event.id || 'default';
  const { otpStartedAtByEventId, setOtpStartedAt } = useSignUpFormStore();
  const [timeLeft, setTimeLeft] = useState(RESEND_COOLDOWN_SECONDS);
  const [canResend, setCanResend] = useState(false);
  const { mutate: requestOtp } = useRequestOtp();
  const [, setSnackbar] = useContext(AlertSnackbarContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!otpStartedAtByEventId[eventKey]) {
      setOtpStartedAt(eventKey, Date.now());
    }
  }, [eventKey, otpStartedAtByEventId, setOtpStartedAt]);

  // Recompute remaining time whenever the persisted start time changes
  useEffect(() => {
    const startedAt = otpStartedAtByEventId[eventKey];
    if (!startedAt) return;

    const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
    const remaining = RESEND_COOLDOWN_SECONDS - elapsedSeconds;
    const safeRemaining = remaining > 0 ? remaining : 0;

    setTimeLeft(safeRemaining);
    setCanResend(safeRemaining <= 0);
  }, [eventKey, otpStartedAtByEventId]);

  useEffect(() => {
    if (event?.rsvpStatus === EventInviteStatus.GOING) {
      navigate(`/discover/${event.id}`);
    }
  }, [event?.rsvpStatus]);

  const handleSubmit = (values: Partial<VerifyOtpForm>) => {
    if (isSigningUp) return;

    const params = {
      eventId: event.id,
      email: formValue.email,
      code: values.code,
      fullname: formValue.fullname,
      phone: formValue.phone,
    } as SignUpToEventDto;

    mutate(params, {
      onSuccess: () => {
        onFinish?.();
      },
      onError: (error: any) => {
        setSnackbar({
          message: error?.message || ErrorMessageStrings.FAILED_TO_SIGN_UP,
          severity: 'error',
        });
      },
    });
  };

  // Countdown timer effect
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }

    setCanResend(false);

    const timer = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds} ${VerifyOtpStrings.SEC}${seconds !== 1 ? 's' : ''}`;
    }

    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const time = minutes > 1 ? VerifyOtpStrings.MINS : VerifyOtpStrings.SECS;
    return `${minutes}:${secs.toString().padStart(2, '0')} ${time}`;
  };

  const handleResendOtp = () => {
    if (!canResend) return;
    requestOtp(
      { phone: formValue.phone, isSignUp: isSignUp },
      {
        onSuccess: () => {
          setOtpStartedAt(eventKey, Date.now());
          setTimeLeft(RESEND_COOLDOWN_SECONDS);
          setCanResend(false);
          setSnackbar({
            message: AlertMessages.VERIFICATION_CODE_RESENT_SUCCES,
            severity: 'success',
          });
        },
        onError: (error) => {
          setSnackbar({
            message: error.message,
            severity: 'error',
          });
        },
      },
    );
  };

  const entryFee = event.entryFee > 0 ? ButtonStrings.BUTTON_PAY_NOW_WITH_FEE + event.entryFee : ButtonStrings.BUTTON_SIGN_UP;

  return (
    <Container>
      <StyledCard>
        <Typography variant="h6" style={{ fontWeight: 700, alignSelf: 'center' }}>
          {VerifyOtpStrings.TITLE}
        </Typography>
        <Formik initialValues={initialValues} validationSchema={validationSchema} validateOnChange={false} onSubmit={handleSubmit}>
          {({ values, handleChange, errors, touched }) => {
            return (
              <Form>
                <Box display="grid" gap={3}>
                  <FieldContainer>
                    <Label>{VerifyOtpStrings.ENTER_CODE}</Label>
                    <StyledDescription>
                      {VerifyOtpStrings.DESCRIPTION}
                      <ResendButton onClick={handleResendOtp}>
                        {canResend ? ButtonStrings.BUTTON_RESEND : `${ButtonStrings.BUTTON_RESEND_IN} ${formatTime(timeLeft)}`}
                      </ResendButton>
                    </StyledDescription>

                    <TextField
                      name="code"
                      fullWidth
                      value={values.code}
                      onChange={handleChange}
                      error={touched.code && !!errors.code}
                      helperText={touched.code && errors.code}
                    />
                  </FieldContainer>

                  <LoadingButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    style={{ opacity: !values.code ? 0.5 : 1.0 }}
                    disabled={isSigningUp}
                    loading={isSigningUp}
                  >
                    {entryFee}
                  </LoadingButton>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </StyledCard>
    </Container>
  );
};

export default memo(VerifyOtp);

const Container = styled('div')({
  background: Color.cultured,
  borderRadius: '16px',
});

const StyledCard = styled(Card)({
  maxWidth: 400,
  display: 'flex',
  flexDirection: 'column',
  padding: 24,
  borderRadius: 16,
  alignSelf: 'center',
  margin: '0 auto',
  gap: 16,
});

const FieldContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: 12,
});

const Label = styled(Typography)({
  fontWeight: 700,
});

const StyledDescription = styled(Typography)({
  fontSize: '14px',
});

const ResendButton = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: Color.prussianBlue,
  cursor: 'pointer',
});
